/**
 * The assistant conducting an interview instead of deflecting.
 *
 * The bug this replaces: a user attached a folder, typed "then convert this to
 * 4 classes", and was told to go and do it in Project Management. Two things
 * were wrong. The sentence did not parse — fixed in `datasetIntent.js` — and,
 * more importantly, **the fall-through was to the chat path at all**. No verb
 * list is ever complete, so the fallback must be to ASK, never to explain.
 *
 * Shape of the conversation, and why it is this short:
 *
 *   step 0  scan        state the counts and the caps. Not a question.
 *   Q1      task        classify / detect / import-only. SKIPPED when the
 *                       instruction or the archive already answered it.
 *   Q2      grouping    classification: how many groups. SKIPPED when the
 *                       folders already ARE the classes, or a count was typed.
 *           objects     detection: which objects to find. NEVER skipped —
 *                       there is no safe default for a vocabulary.
 *   step 3  work        cluster, or auto-box. Still nothing created.
 *   Q3      review      what came out, with pictures. Free and repeatable.
 *   Q4      confirm     the ONE irreversible moment, fully enumerated.
 *
 * **Names are not questions.** Project and model names are derived from the
 * file and edited inline on the confirm card. Asking for them up front is three
 * blocking turns for the decision a user can most easily change, at the moment
 * they trust the machine least — and `resolveProject`/`resolveModel` already
 * handle collisions.
 *
 * **Nothing is asked that the user already said.** Every answer the instruction
 * or the archive supplies is pre-filled and shown as a summary line, so the
 * competent user who typed a complete sentence goes straight to the confirm.
 *
 * Pure: no React, no network, no File. `advance` is the only way state moves.
 */
import { MAX_IMPORT_BYTES, MAX_IMPORT_IMAGES, evenSample } from './datasetScan';
import { readIntent } from './datasetIntent';
import { cleanName, nameFromFile, uniqueClassNames } from './datasetPlan';

export const STAGES = Object.freeze(['ask', 'work', 'review', 'confirm', 'running', 'done']);

/**
 * Segmentation is deliberately absent.
 *
 * Nothing in the product can generate a mask: the auto-detect module's FastSAM
 * stage returns class-agnostic BOXES, and `save_segmentation_data` is not wired
 * into the assistant at all. Offering it would be six questions whose every
 * answer is discarded. It is answered in words instead.
 */
export const TASKS = Object.freeze(['classification', 'detection', 'manual']);

export const GROUPING_CHOICES = Object.freeze(['auto', 'few', 'many', 'named']);

/**
 * Measured against the live stack on 2026-09-11: yolov8s-worldv2 at 640px,
 * warm, answered in 0.25s per image (six sequential calls, median 0.25s).
 * The first call is ~11s because it loads the model.
 */
const SECONDS_PER_IMAGE = 0.25;
const AUTO_BOX_LANES = 3;

/** What each grouping choice means to `/cluster/auto`. */
const GROUPING_TO_PLAN = {
  auto: { numClasses: null, maxClasses: null },
  few: { numClasses: null, maxClasses: 4 },
  many: { numClasses: null, maxClasses: 8 },
};

const INTENT_TO_TASK = {
  divide: 'classification',
  boxes: 'detection',
  build: null,
  segment: null,
  ambiguous: null,
  question: null,
};

/**
 * Choose what to import, under BOTH caps.
 *
 * `overByteCap` was computed by the scan and read by nothing — every limit the
 * user saw was priced in image count, so a 300-image zip of phone photos could
 * sail past the byte limit into the one multipart request that cannot be
 * chunked, and fail after the project and model had already been created.
 */
export const selectImportable = (images = []) => {
  const byCount = evenSample(images, MAX_IMPORT_IMAGES);
  const kept = [];
  let bytes = 0;
  byCount.forEach((image) => {
    const size = image.size || 0;
    if (bytes + size > MAX_IMPORT_BYTES) return;
    kept.push(image);
    bytes += size;
  });
  return {
    images: kept,
    bytes,
    droppedForCount: Math.max(0, images.length - byCount.length),
    droppedForBytes: byCount.length - kept.length,
  };
};

/**
 * Start an interview.
 *
 * Everything the instruction and the archive already say is filled in here, so
 * the questions that remain are exactly the ones nobody has answered.
 */
export const createInterview = ({ scan, meta, instruction, fileName, projects }) => {
  const intent = readIntent(instruction);
  const annotated = !!(meta && (meta.format === 'yolo' || meta.format === 'coco'));
  const hasFolders = scan.layout === 'folder_per_class' && (scan.groups || []).length >= 2;
  const selection = selectImportable(scan.images || []);
  const baseName = nameFromFile(fileName);

  const answers = {
    // The ARCHIVE wins over anything typed: a zip carrying YOLO labels is a
    // detection dataset whatever the sentence said. Letting the text override
    // it would point an annotated dataset at the classification endpoint,
    // which drops every box.
    task: annotated ? 'detection' : INTENT_TO_TASK[intent.intent] || null,
    useArchiveLabels: annotated,
    grouping: null,
    count: intent.count || null,
    maxCount: intent.maxCount || null,
    classNames: null,
    objects: intent.objects.length ? intent.objects : null,
    projectId: null,
    projectName: cleanName(intent.projectName, baseName),
    modelName: null,
  };

  // The folders already ARE the classes — there is nothing to ask.
  if (!annotated && hasFolders && !intent.count && !intent.maxCount) {
    answers.task = answers.task || 'classification';
    answers.grouping = 'folders';
  }
  if (answers.count || answers.maxCount || (answers.classNames || []).length) {
    answers.grouping = answers.grouping || 'explicit';
  }

  const draft = {
    version: 1,
    stage: 'ask',
    answers,
    intent: intent.intent,
    // What the user typed, kept so the confirm card can show it back.
    instruction: (instruction || '').trim(),
    facts: {
      totalImages: scan.totalImages,
      importable: selection.images.length,
      importBytes: selection.bytes,
      droppedForCount: selection.droppedForCount,
      droppedForBytes: selection.droppedForBytes,
      layout: scan.layout,
      folderNames: (scan.groups || []).map((group) => group.name),
      annotationFormat: (meta && meta.format) || null,
      archiveClasses: (meta && meta.names) || [],
      fileName: fileName || null,
      baseName,
    },
    projects: (projects || []).map((project) => ({ id: project.id, name: project.name })),
    result: null,
    error: null,
  };

  /*
   * An instruction can answer EVERYTHING, and then there is nothing to ask.
   *
   * "4 classes bnao" fills task and count; "detect helmet and person" fills
   * task and objects. `currentQuestion` then returns null while the stage is
   * still `ask`, and the card rendered its header and its summary and NOTHING
   * ELSE — no question, no button, no way forward. The user had said exactly
   * what they wanted and the assistant simply stopped.
   *
   * `answerQuestion` has always ended this way; `createInterview` did not, so
   * the bug only appeared when the FIRST evaluation had no questions left —
   * which is precisely the case where the user was clearest.
   */
  return currentQuestion(draft) ? draft : { ...draft, stage: nextStageFor(draft) };
};

/** Does this state still need the task question? */
const needsTask = (state) => !state.answers.task;

const needsGrouping = (state) =>
  state.answers.task === 'classification' && !state.answers.grouping;

const needsObjects = (state) =>
  state.answers.task === 'detection' &&
  !state.answers.useArchiveLabels &&
  !(state.answers.objects || []).length;

/**
 * The question to put on screen, or null when there is nothing left to ask.
 *
 * `kind` is `choice` or `text`; a `choice` may still accept typed input, which
 * is parsed against THIS question's domain only — never against the global
 * intent parser, which is how a wizard traps a user inside itself.
 */
export const currentQuestion = (state) => {
  if (!state || state.stage !== 'ask') return null;

  if (needsTask(state)) {
    const options = [
      { id: 'classification', labelKey: 'assistant.interview.task.classification' },
      { id: 'detection', labelKey: 'assistant.interview.task.detection' },
      { id: 'manual', labelKey: 'assistant.interview.task.manual' },
    ];
    return {
      id: 'task',
      kind: 'choice',
      promptKey: 'assistant.interview.ask.task',
      options,
    };
  }

  if (needsGrouping(state)) {
    return {
      id: 'grouping',
      kind: 'choice',
      promptKey: 'assistant.interview.ask.grouping',
      options: [
        // The server picks k by silhouette score when none is given, and its
        // answer is better than a guess. Offering integers first guarantees
        // the "I asked for 5 and got 4" disappointment.
        { id: 'auto', labelKey: 'assistant.interview.grouping.auto' },
        { id: 'few', labelKey: 'assistant.interview.grouping.few' },
        { id: 'many', labelKey: 'assistant.interview.grouping.many' },
        { id: 'named', labelKey: 'assistant.interview.grouping.named' },
      ],
      accepts: 'count',
    };
  }

  if (needsObjects(state)) {
    return {
      id: 'objects',
      kind: 'text',
      promptKey: 'assistant.interview.ask.objects',
      // Seeded from the folder names — often exactly what the user wants found.
      suggestions: state.facts.folderNames.slice(0, 6),
      // The ONLY question in the flow that commits the user to GPU time, and
      // the only one that quotes a cost. The per-image figure is MEASURED
      // against the deployed stack (0.25s warm for yolov8s-worldv2 at 640px,
      // 2026-09-11) rather than guessed — a made-up number that is wrong by 5x
      // is worse than no number.
      costKey: 'assistant.interview.ask.objectsCost',
      costParams: {
        images: state.facts.importable,
        seconds: Math.max(5, Math.round((state.facts.importable * SECONDS_PER_IMAGE) / AUTO_BOX_LANES)),
      },
    };
  }

  return null;
};

/** Parse a typed answer against ONE question's domain. Never the global parser. */
export const parseAnswer = (question, raw) => {
  const text = String(raw || '').trim();
  if (!text) return { ok: false };

  if (question.id === 'task') {
    const lower = text.toLowerCase();
    if (/^\d+$/.test(lower)) return { ok: false };
    if (/class|group|sort|분류|나눠/i.test(lower)) return { ok: true, value: 'classification' };
    if (/detect|box|object|검출|박스/i.test(lower)) return { ok: true, value: 'detection' };
    if (/manual|myself|self|import|직접/i.test(lower)) return { ok: true, value: 'manual' };
    return { ok: false };
  }

  if (question.id === 'grouping') {
    const number = text.match(/\b(\d+)\b/);
    if (number) {
      const count = Math.min(12, Math.max(2, Number(number[1])));
      return { ok: true, value: 'explicit', count };
    }
    const lower = text.toLowerCase();
    if (/auto|you|best|khud|알아서/i.test(lower)) return { ok: true, value: 'auto' };
    // A comma/and list is the user naming the classes.
    const names = text.split(/\s*(?:,|\band\b|\baur\b|\/)\s*/i).map((part) => part.trim()).filter(Boolean);
    if (names.length >= 2) {
      return { ok: true, value: 'explicit', classNames: uniqueClassNames(names).slice(0, 12) };
    }
    return { ok: false };
  }

  if (question.id === 'objects') {
    const names = text
      .split(/\s*(?:,|\band\b|\baur\b|\/|\+|이랑|랑)\s*/i)
      .map((part) => part.trim())
      .filter((part) => part && part.length <= 40);
    if (!names.length) return { ok: false };
    return { ok: true, value: names.slice(0, 12) };
  }

  return { ok: false };
};

/** Apply an answer. Returns a NEW state; never mutates. */
export const answerQuestion = (state, questionId, value, extra = {}) => {
  const answers = { ...state.answers };

  if (questionId === 'task') {
    answers.task = TASKS.includes(value) ? value : answers.task;
    // Changing the task invalidates everything downstream of it.
    answers.grouping = null;
    answers.objects = null;
    if (answers.task === 'manual') answers.grouping = 'unlabeled';
  } else if (questionId === 'grouping') {
    answers.grouping = value;
    if (extra.count) answers.count = extra.count;
    if (extra.classNames) {
      answers.classNames = extra.classNames;
      answers.count = extra.classNames.length;
    }
  } else if (questionId === 'objects') {
    answers.objects = Array.isArray(value) ? value : [value];
  }

  // Back to `ask` BEFORE working out what is left. Re-answering a question
  // from a later stage (the user changed their mind after the work ran) must be
  // able to re-open the questions that answer invalidated — and
  // `currentQuestion` deliberately returns null unless the stage is `ask`, so
  // evaluating it against the old stage silently skips them.
  const next = { ...state, answers, stage: 'ask', result: null, error: null };
  const remaining = currentQuestion(next);
  return remaining ? next : { ...next, stage: nextStageFor(next) };
};

/**
 * What happens once the questions run out.
 *
 * Classification with real folders and manual import have no expensive step, so
 * they go straight to the confirm. Clustering and auto-boxing do work first —
 * and that work must happen BEFORE anything is created, so a failure leaves the
 * account untouched.
 */
const nextStageFor = (state) => {
  const { task, grouping } = state.answers;
  if (task === 'manual' || grouping === 'folders' || state.answers.useArchiveLabels) return 'confirm';
  return 'work';
};

/** Go back to a question that was already answered, discarding what it invalidates. */
export const reopenQuestion = (state, questionId) => {
  const answers = { ...state.answers };
  if (questionId === 'task') {
    answers.task = null;
    answers.grouping = null;
    answers.objects = null;
  } else if (questionId === 'grouping') {
    answers.grouping = null;
    answers.count = null;
    answers.classNames = null;
  } else if (questionId === 'objects') {
    answers.objects = null;
  }
  const draft = { ...state, answers, stage: 'ask', result: null, error: null };
  /*
   * Same rule as `createInterview` and `answerQuestion`: never hand back a card
   * that is on `ask` with nothing to ask. Clearing an answer does not always
   * open a question — reopening `grouping` on a detection interview clears a
   * field that only classification ever asks about — and the card then rendered
   * its header and NOTHING else, with no way forward.
   */
  return currentQuestion(draft) ? draft : { ...draft, stage: nextStageFor(draft) };
};

/** Every answered question, for the summary lines on the card. */
export const answeredSummary = (state) => {
  const rows = [];
  const { answers } = state;
  if (answers.task) {
    rows.push({
      id: 'task',
      labelKey: 'assistant.interview.summary.task',
      valueKey: `assistant.interview.task.${answers.task}`,
      // An answer the ARCHIVE made is not the user's to change.
      locked: !!answers.useArchiveLabels,
    });
  }
  /*
   * Grouping is a CLASSIFICATION concept. A detection interview still carries
   * `grouping: 'explicit'` — createInterview sets it from any count, and
   * "detect helmet and person" counts two — so the card grew a "Grouping: 2
   * group(s)" row that means nothing for boxes, and whose Change button led
   * nowhere: reopening it cleared the answer, and `needsGrouping` only fires
   * for classification, so there was no question left to ask.
   */
  if (answers.grouping && answers.task !== 'detection') {
    rows.push({
      id: 'grouping',
      labelKey: 'assistant.interview.summary.grouping',
      valueKey: `assistant.interview.grouping.${
        ['auto', 'few', 'many', 'named', 'folders', 'explicit', 'unlabeled'].includes(answers.grouping)
          ? answers.grouping
          : 'auto'
      }`,
      params: { count: answers.count || 0 },
      locked: answers.grouping === 'folders',
    });
  }
  if ((answers.objects || []).length) {
    rows.push({
      id: 'objects',
      labelKey: 'assistant.interview.summary.objects',
      value: answers.objects.join(', '),
    });
  }
  return rows;
};

/** The plan the runner executes, derived from the answers. */
export const planFromInterview = (state, options = {}) => {
  const { answers, facts } = state;
  const detection = answers.task === 'detection';
  const baseName = facts.baseName;

  let mode = 'unlabeled';
  if (answers.useArchiveLabels) mode = 'annotations';
  else if (detection) mode = 'annotations';
  else if (answers.grouping === 'folders') mode = 'folders';
  else if (answers.task === 'classification' && answers.grouping !== 'unlabeled') mode = 'cluster';

  const grouping = GROUPING_TO_PLAN[answers.grouping] || { numClasses: null, maxClasses: null };
  const numClasses = answers.count || grouping.numClasses;
  const maxClasses = numClasses ? null : answers.maxCount || grouping.maxClasses;

  let classes = [];
  if (mode === 'folders') {
    const names = uniqueClassNames(facts.folderNames);
    classes = names.map((name, index) => ({ id: index + 1, name, count: null, source: facts.folderNames[index] }));
  } else if (mode === 'annotations') {
    const names = uniqueClassNames(
      answers.useArchiveLabels ? facts.archiveClasses : answers.objects || [],
    );
    classes = names.map((name, index) => ({ id: index + 1, name, count: null, source: String(index) }));
  } else if (mode === 'cluster') {
    const names = answers.classNames
      ? uniqueClassNames(answers.classNames)
      : Array.from({ length: numClasses || 0 }, (_, index) => `class ${index + 1}`);
    classes = names.map((name, index) => ({ id: index + 1, name, count: null, source: null }));
  }

  return {
    version: 1,
    action: detection ? 'build_detection' : 'build_classification',
    target: {
      projectName: cleanName(options.projectName, answers.projectName || baseName),
      projectId: options.projectId !== undefined ? options.projectId : answers.projectId,
      modelName: cleanName(
        options.modelName,
        answers.modelName || `${baseName} ${detection ? 'detector' : 'classifier'}`,
      ),
      modelType: detection ? 'bounding_box' : 'classification',
    },
    grouping: { mode, numClasses: numClasses || null, maxClasses: maxClasses || null },
    classes,
    limits: {
      totalImages: facts.totalImages,
      willImport: facts.importable,
      dropped: facts.droppedForCount + facts.droppedForBytes,
    },
    source: { fileName: facts.fileName, layout: facts.layout, format: facts.annotationFormat },
    summary: '',
  };
};

/**
 * A markdown mirror of the interview, stored as the card's `content`.
 *
 * Not decoration. `toHistory` drops a message whose trimmed content is empty,
 * the sidebar searches on it, `deriveTitle` names the thread from it, and the
 * Markdown export prints `_(no content)_` without it. A card that renders
 * beautifully and stores an empty string is invisible to all four.
 */
export const interviewMirror = (state, t) => {
  if (!state) return '';
  const lines = [];
  const { answers, facts } = state;

  lines.push(t('assistant.interview.facts', {
    images: facts.totalImages,
    size: `${Math.max(1, Math.round(facts.importBytes / 1024))} KB`,
  }));

  if (answers.task) lines.push(`**${t('assistant.interview.summary.task')}:** ${t(`assistant.interview.task.${answers.task}`)}`);
  if ((answers.objects || []).length) {
    lines.push(`**${t('assistant.interview.summary.objects')}:** ${answers.objects.join(', ')}`);
  }
  if (state.outcome && state.outcome.modelName) {
    lines.push(t('assistant.build.mirror.done', {
      project: state.outcome.projectName,
      model: state.outcome.modelName,
      imported: state.outcome.imported,
    }));
  }
  return lines.filter(Boolean).join('\n\n') || t('assistant.interview.title');
};

/** Below this, a class has too few examples for training to mean anything. */
export const MIN_IMAGES_PER_CLASS = 10;

/**
 * Whether training may be offered, and why not.
 *
 * Five preconditions, all checkable without another request:
 *  - something must actually be labeled — training filters `classId != "0"`
 *    and raises `No structured items` when nothing survives;
 *  - at least two classes, or the model learns to answer "yes" to everything;
 *  - enough images per class to survive a 70/20/10 split;
 *  - the version must have verified `done`, not `partial` — `partial` means the
 *    background upload lost images, so training would use an incomplete set.
 *
 * Returns `{}` when training is safe to offer.
 */
export const trainingGate = (outcome, plan, t, extra = {}) => {
  if (!outcome || !outcome.versionId) {
    return { trainBlockedKey: 'assistant.interview.finish.blockedNoLabels' };
  }
  if (outcome.status === 'partial') {
    return { trainBlockedKey: 'assistant.interview.finish.blockedPartial' };
  }
  const classes = (plan && plan.classes) || [];
  if (classes.length < 2) {
    return { trainBlockedKey: 'assistant.interview.finish.blockedOneClass' };
  }
  /*
   * For DETECTION, "labeled" means images that actually carry a box — not
   * images that arrived.
   *
   * YOLO-World returning nothing for the words the user chose still imports
   * every image perfectly, so `outcome.imported` was 300 and training was
   * offered on a dataset with ZERO boxes. That trains a detector that detects
   * nothing, and the failure only shows up after the GPU time is spent.
   */
  const isDetection = !!plan && plan.target && plan.target.modelType === 'bounding_box';
  const labeled = isDetection && extra.labeledImages != null
    ? extra.labeledImages
    : outcome.imported || 0;
  if (!labeled) {
    return { trainBlockedKey: 'assistant.interview.finish.blockedNoLabels' };
  }
  const perClass = Math.floor(labeled / classes.length);
  if (perClass < MIN_IMAGES_PER_CLASS) {
    return {
      trainBlockedKey: 'assistant.interview.finish.blockedTooFew',
      trainBlockedParams: { count: perClass },
    };
  }
  return {};
};
